 

import { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Paper, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody,
  Grid, TextField, Button, MenuItem, Dialog, Chip, Divider,
  Collapse, IconButton, TableContainer
} from "@mui/material";
import ExpandMoreIcon   from "@mui/icons-material/ExpandMore";
import ExpandLessIcon   from "@mui/icons-material/ExpandLess";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon   from "@mui/icons-material/TableChart";
import SearchIcon       from "@mui/icons-material/Search";
import RefreshIcon      from "@mui/icons-material/Refresh";

 

 
import { stockService }       from "../service/CreateStockService";
import api                    from "../service/api";
import { exportStockToExcel } from "../component/Stockexcelexport";
import { exportStockToPdf }   from "../component/Stockpdfexport";

/* ─── helpers ──────────────────────────────────────────────────────────────── */
const fmt = (v) =>
  v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const fmtPrice = (v) =>
  v != null ? `₹${Number(v).toLocaleString("en-IN")}` : "—";

const EMPTY_FILTERS = {
  vehicleType: "", companyName: "", model: "", colour: "",
  batteryType: "", location: "", source: "", status: "",
  engine: "", chasis: "",
  fromDate: "", toDate: "", month: "", year: "",
  search: "",
};

const STATUS_COLOR  = { IN_STOCK: "success", SOLD: "error" };
const VEHICLE_COLOR = { E_RICKSHAW: "success", SCOOTY: "secondary" };
const VEHICLE_LABEL = { E_RICKSHAW: "E-Rickshaw", SCOOTY: "Scooty" };

/* ─── component ────────────────────────────────────────────────────────────── */
const AvailableStockView = () => {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const [openView, setOpenView] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewLoad, setViewLoad] = useState(false);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [expandedLocs, setExpandedLocs] = useState({});

  const toggleLoc = (loc) =>
    setExpandedLocs((prev) => ({ ...prev, [loc]: !prev[loc] }));

  const handleChange = (e) =>
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  /* ── active filter label ── */
  const activeFilterLabel = () => {
    const parts = [];
    if (filters.vehicleType) parts.push(VEHICLE_LABEL[filters.vehicleType] || filters.vehicleType);
    if (filters.location)    parts.push(filters.location);
    if (filters.status)      parts.push(filters.status);
    if (filters.fromDate && filters.toDate) parts.push(`${filters.fromDate} → ${filters.toDate}`);
    else if (filters.month && filters.year)
      parts.push(`${new Date(0, filters.month - 1).toLocaleString("default", { month: "long" })} ${filters.year}`);
    else if (filters.year) parts.push(`Year ${filters.year}`);
    return parts.length ? parts.join(" | ") : "All EV Stock";
  };

  /* ── fetch ── */
  const fetchStock = useCallback(async () => {
    try {
      setLoading(true);
      // Build query string matching filterEv API params
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      params.append("limit", "9999");
      params.append("page",  "1");

      const res   = await api.get(`/filter-bikes?${params.toString()}`);
      const bikes = res?.data?.data || [];

      // Group by location
      const grouped = {};
      bikes.forEach((bike) => {
        const loc = bike.location || "Unknown";
        if (!grouped[loc]) grouped[loc] = { location: loc, bikes: [], total: 0 };
        grouped[loc].bikes.push(bike);
        grouped[loc].total++;
      });

      setData(Object.values(grouped).sort((a, b) => a.location.localeCompare(b.location)));
      setError("");
    } catch (err) {
      console.error("fetchStock error:", err);
      setError("Failed to load stock data");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchStock(); }, []); // initial load

  /* ── reset ── */
  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setTimeout(fetchStock, 0);
  };

  /* ── view detail ── */
  const handleView = async (id) => {
    try {
      setOpenView(true);
      setViewData(null);
      setViewLoad(true);
      const res = await api.get(`/specific-bike-details/${id}`);
      setViewData(res?.data?.data || null);
      console.log(res.data.data)
    } catch {
      setError("Failed to load details");
    } finally {
      setViewLoad(false);
    }
  };

  const totalBikes = data.reduce((s, loc) => s + loc.total, 0);

  const rowBg = (status) => {
    if (status === "SOLD") return "#fff5f5";
    return "inherit";
  };

  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", px: 2 }}>

      {/* ── HEADER ── */}
      <Box display="flex" alignItems="center" justifyContent="space-between"
        mb={2} flexWrap="wrap" gap={1} pt={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>EV Stock Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">{activeFilterLabel()}</Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="contained" startIcon={<TableChartIcon />}
            onClick={() => exportStockToExcel(data, filters, activeFilterLabel())}
            disabled={data.length === 0}
            sx={{ bgcolor: "#1d6f42", "&:hover": { bgcolor: "#155a34" }, fontWeight: 700, textTransform: "none" }}
          >
            Export Excel
          </Button>
          <Button
            variant="contained" startIcon={<PictureAsPdfIcon />}
            onClick={() => exportStockToPdf(data, filters, activeFilterLabel())}
            disabled={data.length === 0}
            sx={{ bgcolor: "#c62828", "&:hover": { bgcolor: "#a31515" }, fontWeight: 700, textTransform: "none" }}
          >
            Export PDF
          </Button>
        </Box>
      </Box>

      {/* ── FILTERS ── */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }} elevation={2}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5} color="text.secondary">
          FILTERS
        </Typography>
        <Grid container spacing={1.5}>

          <Grid item xs={6} sm={4} md={2}>
            <TextField select label="Vehicle Type" name="vehicleType" fullWidth size="small"
              value={filters.vehicleType} onChange={handleChange}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="E_RICKSHAW">E-Rickshaw</MenuItem>
              <MenuItem value="SCOOTY">Scooty</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField label="Company Name" name="companyName" fullWidth size="small"
              value={filters.companyName} onChange={handleChange} />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField label="Model" name="model" fullWidth size="small"
              value={filters.model} onChange={handleChange} />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField label="Colour" name="colour" fullWidth size="small"
              value={filters.colour} onChange={handleChange} />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField select label="Battery Type" name="batteryType" fullWidth size="small"
              value={filters.batteryType} onChange={handleChange}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Lithium">Lithium</MenuItem>
              <MenuItem value="Lead">Lead</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField select label="Location" name="location" fullWidth size="small"
              value={filters.location} onChange={handleChange}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Chaitanyapur">Chaitanyapur</MenuItem>
              <MenuItem value="Boroda">Boroda</MenuItem>
              <MenuItem value="Mahisadal">Mahisadal</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField select label="Source" name="source" fullWidth size="small"
              value={filters.source} onChange={handleChange}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="NEW">NEW</MenuItem>
              <MenuItem value="EXCHANGE">EXCHANGE</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField select label="Status" name="status" fullWidth size="small"
              value={filters.status} onChange={handleChange}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="IN_STOCK">IN STOCK</MenuItem>
              <MenuItem value="SOLD">SOLD</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField label="Engine No" name="engine" fullWidth size="small"
              value={filters.engine} onChange={handleChange} />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField label="Serial No" name="chasis" fullWidth size="small"
              value={filters.chasis} onChange={handleChange} />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField type="date" label="From Date" name="fromDate" fullWidth size="small"
              InputLabelProps={{ shrink: true }} value={filters.fromDate} onChange={handleChange} />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField type="date" label="To Date" name="toDate" fullWidth size="small"
              InputLabelProps={{ shrink: true }} value={filters.toDate} onChange={handleChange} />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField select label="Month" name="month" fullWidth size="small"
              value={filters.month} onChange={handleChange}>
              <MenuItem value="">All</MenuItem>
              {[...Array(12)].map((_, i) => (
                <MenuItem key={i + 1} value={i + 1}>
                  {new Date(0, i).toLocaleString("default", { month: "long" })}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField type="number" label="Year" name="year" fullWidth size="small"
              inputProps={{ min: 2020, max: 2099 }}
              value={filters.year} onChange={handleChange} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Global Search (model / engine / Serial No / company)" name="search" fullWidth size="small"
              value={filters.search} onChange={handleChange} />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Button fullWidth variant="contained" startIcon={<SearchIcon />}
              onClick={fetchStock} sx={{ textTransform: "none" }}>
              Apply
            </Button>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Button fullWidth variant="outlined" startIcon={<RefreshIcon />}
              onClick={resetFilters} sx={{ textTransform: "none" }}>
              Reset
            </Button>
          </Grid>

        </Grid>
      </Paper>

      {/* ── STATUS ── */}
      {loading && (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── SUMMARY CHIPS ── */}
      {!loading && data.length > 0 && (
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          <Chip label={`Total: ${totalBikes} vehicle(s)`} color="primary" variant="filled" />
          {data.map((loc) => (
            <Chip key={loc.location} label={`${loc.location}: ${loc.total}`}
              variant="outlined" size="small" />
          ))}
        </Box>
      )}

      {!loading && data.length === 0 && !error && (
        <Alert severity="info">No vehicles found for the selected filters.</Alert>
      )}

      {/* ── LOCATION-WISE TABLES ── */}
      {!loading && data.map((loc, i) => {
        const isOpen = !!expandedLocs[loc.location];
        return (
          <Paper key={i} sx={{ mb: 2, borderRadius: 2, overflow: "hidden" }} elevation={2}>

            <Box
              onClick={() => toggleLoc(loc.location)}
              sx={{
                px: 2, py: 1.5,
                background: "linear-gradient(90deg,#00695c,#00897b)",
                color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                cursor: "pointer", userSelect: "none",
                "&:hover": { background: "linear-gradient(90deg,#004d40,#00695c)" },
                transition: "background 0.2s"
              }}
            >
              <Typography fontWeight={700} fontSize={15}>📍 {loc.location}</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label={`${loc.total} vehicle(s)`} size="small"
                  sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 700 }} />
                <IconButton size="small" sx={{ color: "#fff", p: 0.25 }}>
                  {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>

            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f0faf8" }}>
                      {[
                        "#","Sl No","Date","Type","Company","Model","Colour",
                        "Engine No","Serial No","Battery","OAM Batt",
                        "Controller No","Transport ₹",
                        "Source","Status","Form22","Invoice No","Actions"
                      ].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: "nowrap", fontSize: 12 }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loc.bikes.map((b, idx) => (
                      <TableRow key={b._id}
                        sx={{ bgcolor: rowBg(b.status), "&:hover": { bgcolor: "#e8f5e9" }, transition: "background 0.15s" }}>
                        <TableCell sx={{ color: "text.secondary", fontSize: 11 }}>{idx + 1}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{b.slNo ?? "—"}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>{fmt(b.date)}</TableCell>
                        <TableCell>
                          <Chip label={VEHICLE_LABEL[b.vehicleType] || b.vehicleType || "—"}
                            color={VEHICLE_COLOR[b.vehicleType] || "default"} size="small"
                            sx={{ fontSize: 10, fontWeight: 700 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{b.companyName || "—"}</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{b.model || "—"}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{b.colour || "—"}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>{b.engine || "—"}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>{b.chasis || "—"}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{b.batteryType || "—"}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{b.oamBattery ? "✅" : "—"}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>{b.controllerNo || "—"}</TableCell>
                        
                        <TableCell sx={{ fontSize: 12 }}>{fmtPrice(b.transportPrice)}</TableCell>
                        <TableCell>
                          <Chip label={b.source || "—"}
                            color={b.source === "NEW" ? "primary" : "warning"} size="small"
                            sx={{ fontSize: 10 }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={b.status || "—"}
                            color={STATUS_COLOR[b.status] || "default"} size="small"
                            sx={{ fontSize: 10, fontWeight: 700 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{b.form22Received ? "✅" : "❌"}</TableCell>
                        <TableCell sx={{ fontSize: 11 }}>{b.invoiceNo || "—"}</TableCell>
                        <TableCell>
                          <Button size="small" variant="outlined"
                            onClick={() => handleView(b._id)}
                            sx={{ textTransform: "none", fontSize: 11, py: 0.25, whiteSpace: "nowrap" }}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Paper>
        );
      })}

      {/* ── DETAIL MODAL ── */}
      <Dialog open={openView} onClose={() => { setOpenView(false); setViewData(null); }}
        fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box p={3} sx={{ background: "#f8fafc" }}>
          <Typography variant="h6" fontWeight={700} mb={0.5}>EV Vehicle — Full Details</Typography>
          <Divider sx={{ mb: 2 }} />

          {viewLoad && (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          )}
          {!viewLoad && !viewData && <Alert severity="info">No data found</Alert>}

        {/* Paste this block replacing the {!viewLoad && viewData && (...)} in your Dialog */}

{!viewLoad && viewData && (
  <Grid container spacing={2}>

    {/* Vehicle Core Info */}
    <Grid item xs={12} md={6}>
      <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={1.5}>🛵 Vehicle Info</Typography>
        {[
          ["Sl No",         viewData?.slNo],
          ["Vehicle Type",  viewData?.vehicleType === "E_RICKSHAW" ? "E-Rickshaw" : viewData?.vehicleType === "SCOOTY" ? "Scooty" : viewData?.vehicleType],
          ["Company",       viewData?.companyName],
          ["Model",         viewData?.model],
          ["Colour",        viewData?.colour],
          ["Engine No",     viewData?.engine],
          ["Serial No",     viewData?.chasis],
          ["Invoice No",    viewData?.invoiceNo],
          ["HSN Code",      viewData?.HSN],
          ["Current Location", viewData?.currentLocation || viewData?.location],
          ["Status",        viewData?.status],
          ["Source",        viewData?.source],
          ["Entry Date",    viewData?.date ? new Date(viewData.date).toLocaleDateString("en-IN") : "—"],
          ["Sale Date",     viewData?.saleDate ? new Date(viewData.saleDate).toLocaleDateString("en-IN") : "—"],
        ].map(([label, val]) => (
          <Box key={label} display="flex" justifyContent="space-between"
            py={0.4} borderBottom="1px solid #f1f5f9">
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight={600}>{val ?? "—"}</Typography>
          </Box>
        ))}
     
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">Transport Price</Typography>
          <Typography variant="body2" fontWeight={600}>
            ₹{Number(viewData?.transportPrice || 0).toLocaleString("en-IN")}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">Total Cost</Typography>
          <Typography variant="body2" fontWeight={700} color="primary.main">
            ₹{Number(viewData?.totalCost || 0).toLocaleString("en-IN")}
          </Typography>
        </Box>
      </Paper>
    </Grid>

    {/* Battery / Warranty */}
    <Grid item xs={12} md={6}>
      <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={1.5}>🔋 Battery & Warranty</Typography>
        {[
          // ["Battery Type",         viewData?.batteryType],
          ["OAM Battery",          viewData?.oamBattery ? "✅ Yes" : "❌ No"],
          // ["Warranty Period",       viewData?.warrantyPeriod ? new Date(viewData.warrantyPeriod).toLocaleDateString("en-IN") : "—"],
          ["Controller No",         viewData?.controllerNo],
          ["Controller Warranty",   viewData?.controllerWarranty +" Months" || "—"],
          ["Motor Warranty",        viewData?.motorWarranty + " Months" || "—"],
          // ["Charger",               viewData?.charger],
          // ["Charger Warranty",      viewData?.chargerWarranty ? new Date(viewData.chargerWarranty).toLocaleDateString("en-IN") : "—"],
          ["Form-22 Received",      viewData?.form22Received ? "✅ Yes" : "❌ No"],
          ["Invoice Status",        viewData?.invoiceStatus ? "✅ Done" : "❌ Pending"],
          // ["Quantity",              viewData?.quantity],
          ["Remarks",               viewData?.remarks],
        ].map(([label, val]) => (
          <Box key={label} display="flex" justifyContent="space-between"
            py={0.4} borderBottom="1px solid #f1f5f9">
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight={600}>{val ?? "—"}</Typography>
          </Box>
        ))}
      </Paper>

      {/* Created By */}
      <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 2, mt: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={1.5}>👤 Created By</Typography>
        {[
          ["Name",  viewData?.createdBy?.name],
          ["Email", viewData?.createdBy?.email],
        ].map(([label, val]) => (
          <Box key={label} display="flex" justifyContent="space-between"
            py={0.4} borderBottom="1px solid #f1f5f9">
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight={600}>{val ?? "—"}</Typography>
          </Box>
        ))}
      </Paper>
    </Grid>

    {/* Location Cost Tracking */}
    {viewData?.locationCost?.length > 0 && (
      <Grid item xs={12}>
        <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={1.5}>📍 Location Cost Tracking</Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f0faf8" }}>
                <TableCell><b>Location</b></TableCell>
                <TableCell><b>Total Cost</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {viewData.locationCost.map((lc, i) => (
                <TableRow key={i}>
                  <TableCell>{lc.location}</TableCell>
                  <TableCell>₹{Number(lc.totalCost || 0).toLocaleString("en-IN")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
    )}

    {/* Transfer History */}
    <Grid item xs={12}>
      <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={1.5}>🔁 Transfer History</Typography>
        {viewData?.transferHistory?.length > 0 ? (
          viewData.transferHistory.map((t, i) => (
            <Box key={i} sx={{
              p: 1.5, mb: 1, borderRadius: 1.5,
              background: "#f8fafc", border: "1px solid #e2e8f0"
            }}>
              <Typography variant="body2" fontWeight={700}>
                {t.fromLocation || "—"} ➝ {t.toLocation || "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Challan: {t.transferChallanNo || "—"} &nbsp;|&nbsp; Cost: ₹{Number(t.transferCost || 0).toLocaleString("en-IN")}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Transferred By: {t.transferredBy?.name || t.transferredBy || "—"}
                &nbsp;|&nbsp;
                Date: {t.date ? new Date(t.date).toLocaleDateString("en-IN") : "—"}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">No transfer history</Typography>
        )}
      </Paper>
    </Grid>

    {/* Sale Info */}
    <Grid item xs={12}>
      <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={1.5}>💰 Sale Info</Typography>
        {viewData?.saleDetails ? (
          <Grid container spacing={1}>
            {[
              ["Customer",     viewData.saleDetails.customerName],
              ["Mobile",       (viewData.saleDetails.mobile || []).join(", ")],
              ["Sale Price",   `₹${Number(viewData.saleDetails.salePrice || 0).toLocaleString("en-IN")}`],
              ["Sale Type",    viewData.saleDetails.saleType],
              ["Invoice No",   viewData.saleDetails.invoiceNo],
              ["Sale Date",    viewData.saleDetails.saleDate ? new Date(viewData.saleDetails.saleDate).toLocaleDateString("en-IN") : "—"],
              ["Vehicle Type", viewData.saleDetails.vehicleType === "E_RICKSHAW" ? "E-Rickshaw" : viewData.saleDetails.vehicleType],
              ["Engine No",    viewData.saleDetails.engineNumber],
              ["Chasis No",    viewData.saleDetails.chasisNumber],
            ].map(([label, val]) => (
              <Grid item xs={12} sm={6} key={label}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="body2" fontWeight={600}>{val || "—"}</Typography>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Alert severity="info" sx={{ py: 0.5 }}>Vehicle not sold yet</Alert>
        )}
      </Paper>
    </Grid>

  </Grid>
)}

          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button variant="contained" onClick={() => { setOpenView(false); setViewData(null); }}
              sx={{ textTransform: "none" }}>
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>

    </Box>
  );
};

export default AvailableStockView;